import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList } from '@dfh-corp-sites/ui'
import { graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import React from 'react'

import { SanityLeadershipGrid, SanityTeamMemberLeadership } from '../../../graphql/gatsby'
import { setAnalyticsEvent } from '../../../lib/analytics/state'
import { getGatsbyImageDataForAsset } from '../../../utils/sanity/image'
import Link from '../../global/Link'

// Extend the type to include order and displayOrder fields
type TeamMemberWithOrder = SanityTeamMemberLeadership & {
  displayOrder?: number
}

export type LeadershipMemberProps = TeamMemberWithOrder & {
  className?: string
}

const LeadershipMember: React.FC<LeadershipMemberProps> = ({
  name,
  title,
  photo,
  slug,
  className,
}) => {
  const image = getGatsbyImageDataForAsset(photo, {
    placeholder: 'blurred',
  })

  const content = (
    <article className={`h-full grid grid-cols-1 ${className}`}>
      <div className="relative w-full h-full">
        {image && (
          <GatsbyImage
            className="w-full h-full"
            class="aspect-square h-auto 2xl:h-[550px] w-full object-cover"
            image={image}
            alt={`Portrait of ${name}${title ? `, ${title}` : ''}`}
          />
        )}
      </div>
      <div>
        <h2 className="text-[40px] text-brand-navy mt-5 tracking-tight font-semibold ">{name}</h2>
        <span className="tracking-tight text-xl font-bold text-brand-navy-2">{title}</span>
      </div>
    </article>
  )

  if (slug?.current) {
    const href = `/our-story/leadership/${slug.current}`
    const setAnalyticsEventOnClick = () => {
      setAnalyticsEvent({
        event: 'select_content',
        content_type: 'team_member',
        item_id: `leadership-member-${slug.current}`,
        title: name || '',
        label_text: name || '',
        link_destination: href,
        module_name: 'LeadershipGrid',
      })
    }

    return (
      <Link
        className="bg-white md:p-2.5 px-2.5 py-2.5 border border-[#CFCECE] border-b-0 border-l-0"
        to={href}
        aria-label={`View details for ${name}${title ? `, ${title}` : ''}`}
        onClick={setAnalyticsEventOnClick}
      >
        {content}
      </Link>
    )
  }

  return content
}

interface TempLeadershipGrid extends SanityLeadershipGrid {
  members?: SanityTeamMemberLeadership[]
}

export type LeadershipGridProps = TempLeadershipGrid

export const LeadershipGrid: React.VFC<LeadershipGridProps> = (props) => {
  const { members } = props as { members: TeamMemberWithOrder[] }

  if (!members || members.length === 0) {
    return null
  }

  // Sort members by displayOrder only
  const sortedMembers = [...members].sort((a, b) => {
    const orderA = a.displayOrder ?? 999
    const orderB = b.displayOrder ?? 999
    return orderA - orderB
  })

  return (
    <main id="main-content" tabIndex={-1} className="outline-none">
      <div className="flex flex-col gap-2 mb-10 mt-6 px-2.5 xl:px-[50px]">
        <Breadcrumb className="[&_ol]:gap-0">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild href="/our-story">
                <Link to="/our-story">Our Story</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <h1 className="xl:text-6xl md:text-[50px] text-[40px] text-brand-navy font-semibold">
          Our Leadership
        </h1>
      </div>
      <section
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 [&>a:nth-child(3n)]:border-r-0"
        aria-label="Leadership team members"
      >
        {sortedMembers.map((member) => {
          return member && <LeadershipMember {...member} key={member._id} />
        })}
      </section>
    </main>
  )
}

export const allLeadershipMembersFragment = graphql`
  fragment allSanityTeamMemberLeadership on Query {
    allSanityTeamMemberLeadership(
      filter: { slug: { current: { ne: null } } }
      sort: { fields: [displayOrder], order: [ASC] }
    ) {
      edges {
        node {
          id
          name
          title
          displayOrder
          slug {
            current
          }
        }
      }
    }
  }
`
